/**
 * The review card's own pieces ("Led AI - Daily Report Editor", review
 * screen), shared by Procore, Autodesk and unlinked projects: the kind chip
 * and its menu, the one-line row a decided record folds into, the card a
 * finished walk ends on, and the glyph each field pill leads with.
 *
 * The Autodesk chip's menu lists the project's Form sections
 * (`AutodeskReviewParts`); everything else moves between Procore's record
 * types or daily-log sections. Both re-kind a record through the backend's
 * convert RPC — the phone's "Change category".
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties } from "react";
import {
  ArrowRight,
  Bandage,
  Check,
  CheckSquare,
  ChevronDown,
  CircleCheck,
  CircleX,
  ClipboardCheck,
  ClipboardList,
  Clock,
  CloudSun,
  Eye,
  FileText,
  Flag,
  Gauge,
  HardHat,
  Hash,
  ListPlus,
  Phone,
  Recycle,
  Trash2,
  TriangleAlert,
  Truck,
  UserRound,
  Wrench,
  X,
  type LucideIcon,
} from "lucide-react";

import { PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS } from "../../../../backend/src/shared/procoreDailyLog";
import { postReviewConvert, type ConsoleItemKind, type ConsoleRow, type ReviewDecidedRowResponse } from "../api";
import { ADR, C, FONT_MONO, RD, RV } from "../tokens";
import { RECORD_KIND_GLYPHS } from "../../../../backend/src/shared/recordChrome";
import type { RecordChipFieldType } from "../../../../backend/src/frontend/components/record/propertyMarks";
import type { ReviewField } from "./ReviewQueue";

export type ChipKind = {
  key: string;
  chip: string;
  menu: string;
  icon: LucideIcon;
  /** The phone's glyph and ink for the record's KIND (`recordChrome`), drawn
   *  on the chip instead of `icon`. */
  kind?: PhoneKind;
};

/** The phone's record kinds, as its type chip draws them (`RecordCard`'s
 *  `KIND_INK`): an observation and a daily log blue, a punch item amber, a
 *  task the one neutral kind. */
export type PhoneKind = "log" | "observation" | "punch" | "task";
export const PHONE_KIND_INK: Record<PhoneKind, string> = {
  log: RD.info,
  observation: RD.info,
  punch: RD.amber,
  task: RD.sub,
};

export function PhoneKindGlyph({ kind, size = 15 }: { kind: PhoneKind; size?: number }) {
  const glyph = RECORD_KIND_GLYPHS[kind];
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={PHONE_KIND_INK[kind]} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flex: "none" }}>
      <path d={glyph.d1} />
      <path d={glyph.d2} />
    </svg>
  );
}

/** The chip's ground: its kind's ink, lightly, on the card. */
export function phoneKindSoft(kind: PhoneKind): string {
  return `color-mix(in srgb, ${PHONE_KIND_INK[kind]} 13%, ${RD.card})`;
}

const RULE = "rgba(20,17,12,.08)";

/** The blue pill a card leads with, and the menu that moves the record. */
export function KindChip({ testId, current, menuLabel, sections, disabled, onPick }: {
  testId: string;
  current: ChipKind;
  menuLabel: string;
  sections: Array<{ heading?: string; kinds: ChipKind[] }>;
  disabled?: boolean;
  /** Resolves to an error sentence, or null when the move landed. */
  onPick: (kind: ChipKind) => Promise<string | null>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLSpanElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  // The menu opens over the page, placed against the chip — inside the card,
  // a long section list ran under the next card.
  const [placed, setPlaced] = useState<{ left: number; top?: number; bottom?: number; maxHeight: number } | null>(null);
  useLayoutEffect(() => {
    if (!open) { setPlaced(null); return; }
    const place = () => {
      const anchor = buttonRef.current?.getBoundingClientRect();
      if (!anchor) return;
      const gutter = 12;
      const left = Math.max(gutter, Math.min(anchor.left, window.innerWidth - gutter - MENU_WIDTH));
      const below = window.innerHeight - anchor.bottom - gutter - 6;
      const above = anchor.top - gutter - 6;
      setPlaced(below >= 240 || below >= above
        ? { left, top: anchor.bottom + 6, maxHeight: Math.min(360, below) }
        : { left, bottom: window.innerHeight - anchor.top + 6, maxHeight: Math.min(360, above) });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!ref.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const Icon = current.icon;
  const ink = current.kind ? PHONE_KIND_INK[current.kind] : ADR.blue;
  const soft = current.kind ? phoneKindSoft(current.kind) : ADR.blueSoft;
  const pick = async (kind: ChipKind) => {
    setOpen(false);
    if (kind.key === current.key) return;
    setBusy(true);
    setError(null);
    const problem = await onPick(kind);
    setBusy(false);
    setError(problem);
  };
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 10, minWidth: 0 }}>
      <button
        ref={buttonRef}
        type="button"
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled || busy}
        onClick={() => setOpen((value) => !value)}
        style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 34, padding: "0 12px", borderRadius: 999, border: 0, background: soft, color: ink, cursor: disabled ? "default" : "pointer", fontFamily: "inherit", opacity: busy ? 0.6 : 1, flex: "none" }}
      >
        {current.kind ? <PhoneKindGlyph kind={current.kind} /> : <Icon size={15} color={ink} aria-hidden />}
        <span style={{ fontFamily: FONT_MONO, fontSize: 11, fontWeight: 700, letterSpacing: "1.6px", textTransform: "uppercase", whiteSpace: "nowrap" }}>{current.chip}</span>
        <ChevronDown size={13} color={ink} aria-hidden />
      </button>
      {error && <span role="alert" style={{ fontSize: 12.5, color: C.red }}>{error}</span>}
      {open && placed && typeof document !== "undefined" && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          aria-label={menuLabel}
          data-testid="review-kind-menu"
          style={{ position: "fixed", left: placed.left, ...(placed.top !== undefined ? { top: placed.top } : { bottom: placed.bottom }), zIndex: 1000, width: MENU_WIDTH, maxHeight: placed.maxHeight, overflowY: "auto", boxSizing: "border-box", background: ADR.paper, border: "1px solid rgba(20,17,12,.14)", borderRadius: 12, boxShadow: "0 14px 30px rgba(22,18,10,.18)", padding: 6 }}
        >
          <div style={menuHeading}>{menuLabel}</div>
          {sections.map((section, index) => (
            <div key={index} style={index > 0 ? { borderTop: "1px solid rgba(20,17,12,.06)", marginTop: 4, paddingTop: 4 } : undefined}>
              {section.heading && <div style={{ ...menuHeading, padding: "4px 10px 2px" }}>{section.heading}</div>}
              {section.kinds.map((kind) => (
                <KindOption key={kind.key} kind={kind} on={kind.key === current.key} onPick={() => void pick(kind)} />
              ))}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </span>
  );
}

const MENU_WIDTH = 260;

const menuHeading: CSSProperties = { fontFamily: FONT_MONO, fontSize: 8.5, fontWeight: 700, letterSpacing: "1.3px", color: C.sub2, padding: "6px 10px 4px", textTransform: "uppercase" };

function KindOption({ kind, on, onPick }: { kind: ChipKind; on: boolean; onPick: () => void }) {
  const Icon = kind.icon;
  return (
    <button
      type="button"
      role="option"
      aria-selected={on}
      onClick={onPick}
      className="review-menu-item"
      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", height: 36, padding: "0 10px", border: 0, borderRadius: 8, background: on ? ADR.menuOn : "transparent", fontSize: 13.5, fontWeight: 500, color: C.ink, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}
    >
      {kind.kind && kind.kind !== "log" ? <PhoneKindGlyph kind={kind.kind} /> : <Icon size={15} color={C.sub} aria-hidden />}
      <span style={{ flex: 1, fontSize: 13.5 }}>{kind.menu}</span>
      {on && <Check size={14} color={C.ink} aria-hidden />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Procore (and unlinked) records
// ---------------------------------------------------------------------------

const RECORD_TYPES: ChipKind[] = [
  { key: "observation", chip: "Observation", menu: "Observation", icon: Eye, kind: "observation" },
  { key: "punch", chip: "Punch item", menu: "Punch item", icon: Flag, kind: "punch" },
  { key: "task", chip: "Task", menu: "Task", icon: CheckSquare, kind: "task" },
];

/** The phone's daily-log sections, the design's five first. */
const LOG_SECTIONS: ChipKind[] = [
  { key: "manpower", chip: "Manpower", menu: "Manpower", icon: HardHat },
  { key: "deliveries", chip: "Delivery", menu: "Deliveries", icon: Truck },
  { key: "equipment", chip: "Equipment", menu: "Equipment", icon: Wrench },
  { key: "inspections", chip: "Inspection", menu: "Inspections", icon: ClipboardCheck },
  { key: "notes", chip: "Notes", menu: "Notes", icon: FileText },
  { key: "visitors", chip: "Visitor", menu: "Visitors", icon: UserRound },
  { key: "phone_calls", chip: "Phone call", menu: "Phone calls", icon: Phone },
  { key: "delays", chip: "Delay", menu: "Delays", icon: Clock },
  { key: "safety_violations", chip: "Safety", menu: "Safety", icon: TriangleAlert },
  { key: "accidents", chip: "Accident", menu: "Accidents", icon: Bandage },
  { key: "dumpsters", chip: "Dumpster", menu: "Dumpsters", icon: Trash2 },
  { key: "work_logs", chip: "Scheduled work", menu: "Scheduled work", icon: ClipboardList },
  { key: "weather", chip: "Weather", menu: "Weather", icon: CloudSun },
  { key: "quantities", chip: "Quantity", menu: "Quantities", icon: Hash },
  { key: "plan_revisions", chip: "Plan revision", menu: "Plan revisions", icon: FileText },
  { key: "productivity", chip: "Productivity", menu: "Productivity", icon: Gauge },
  { key: "waste", chip: "Waste", menu: "Waste", icon: Recycle },
];

type KindSource = Pick<ConsoleRow, "kind" | "category" | "recordType">;

function isIssueLike(item: KindSource): boolean {
  return item.kind !== "log" || item.recordType === "observation" || item.category === "observations";
}

export function procoreKindOf(item: KindSource): ChipKind {
  if (item.kind === "punch") return RECORD_TYPES[1]!;
  if (item.kind === "task") return RECORD_TYPES[2]!;
  if (isIssueLike(item)) return RECORD_TYPES[0]!;
  const category = item.category ?? "notes";
  const label = (PROCORE_DAILY_LOG_CATEGORY_DEFINITIONS as Record<string, { label?: string } | undefined>)[category]?.label
    ?? category.replace(/_/g, " ").replace(/^./, (letter) => letter.toUpperCase());
  // A daily-log entry wears the phone's daily-log glyph; its section is the word.
  const section = LOG_SECTIONS.find((kind) => kind.key === category);
  return { ...(section ?? { key: category, chip: label, menu: label, icon: FileText }), kind: "log" };
}

/**
 * A Procore-format record's kind, and the move to another: an observation,
 * punch item or task changes record type; a daily-log entry changes section.
 */
export function ProcoreKindChip({ item, search, disabled, onConverted }: {
  item: ConsoleRow;
  search: string;
  disabled?: boolean;
  onConverted: () => void;
}) {
  const current = procoreKindOf(item);
  const issue = isIssueLike(item);
  return (
    <KindChip
      testId="review-kind-chip"
      current={current}
      menuLabel={issue ? "Record type" : "Daily log section"}
      sections={[{ kinds: issue ? RECORD_TYPES : LOG_SECTIONS }]}
      disabled={disabled}
      onPick={async (kind) => {
        const result = await postReviewConvert(search, issue
          ? { sourceKind: item.kind, sourceId: item.id, targetKind: kind.key }
          : { sourceKind: "log", sourceId: item.id, targetKind: "log", targetSubcategory: kind.key });
        if (!result.ok) return result.error || "Could not change this record.";
        onConverted();
        return null;
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// A decided record, folded
// ---------------------------------------------------------------------------

export type DecidedState = ReviewDecidedRowResponse["decision"];

/**
 * A decided record, collapsed to one line (design "Review card", decided):
 * a filled round mark, the title, and what happened — `Accepted · syncing`,
 * `Rejected` (struck through), or `Saved for later`.
 */
const STATE: Record<DecidedState, { label: string; mark: string; ink: string; icon: LucideIcon; strike?: boolean }> = {
  accepted: { label: "Accepted · syncing", mark: RV.green, ink: RV.greenInk, icon: Check },
  rejected: { label: "Rejected", mark: ADR.red, ink: ADR.redInk, icon: X, strike: true },
  deferred: { label: "Saved for later", mark: RV.meta, ink: ADR.label, icon: ListPlus },
};

export function DecidedRow({ id, kind, title, decision, canUndo, busy, error, onUndo }: {
  id: string;
  /** The record's kind — named for screen readers; the design draws no chip. */
  kind: ChipKind;
  title: string;
  decision: DecidedState;
  canUndo: boolean;
  busy: boolean;
  error?: string | null;
  onUndo: () => void;
}) {
  const state = STATE[decision];
  const Icon = state.icon;
  return (
    <div data-testid="review-decided-row" data-item-id={id} data-decision={decision} className="review-fade">
      <div style={{ display: "flex", alignItems: "center", gap: 10, height: 52, padding: "0 18px", borderRadius: 18, background: ADR.paper, boxShadow: "0 1px 0 rgba(20,17,12,.04),0 10px 28px rgba(20,17,12,.06)" }}>
        <span aria-hidden style={{ flex: "none", width: 20, height: 20, borderRadius: 10, background: state.mark, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={10} color={ADR.card} strokeWidth={3} />
        </span>
        <span aria-label={`${kind.chip}: ${title}`} style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: C.sub, textDecoration: state.strike ? "line-through" : "none" }}>{title}</span>
        <span data-testid="review-decided-label" style={{ flex: "none", fontFamily: FONT_MONO, fontSize: 9, fontWeight: 700, letterSpacing: "1.1px", textTransform: "uppercase", color: state.ink, whiteSpace: "nowrap" }}>{state.label}</span>
        {canUndo && (
          <button
            type="button"
            data-testid="review-decided-undo"
            disabled={busy}
            onClick={onUndo}
            style={{ flex: "none", height: 26, padding: "0 10px", borderRadius: 999, border: "1px solid rgba(20,17,12,.12)", background: ADR.card, fontSize: 12, fontWeight: 600, color: C.ink, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1, fontFamily: "inherit" }}
          >
            {busy ? "Undoing…" : "Undo"}
          </button>
        )}
      </div>
      {error && <div role="alert" style={{ marginTop: 6, padding: "0 18px", fontSize: 12.5, color: C.red }}>{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The walk, finished
// ---------------------------------------------------------------------------

/**
 * The head of a walk's records set aside for later — the phone's "Save for
 * later" and Later, its list-plus glyph. They are not waiting and not decided:
 * folded away while the walk still has records to review, opened once it does
 * not, and decided like any other card.
 */
export function SavedForLaterHeader({ count, open, onToggle }: { count: number; open: boolean; onToggle: () => void }) {
  return (
    <div data-testid="review-saved-for-later" style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 12px 12px 18px", border: `1px solid ${RULE}`, borderRadius: 14, background: ADR.recess }}>
      <ListPlus size={16} color={C.sub} aria-hidden style={{ flex: "none" }} />
      <span style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "baseline", gap: 8, whiteSpace: "nowrap", overflow: "hidden" }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>Saved for later</span>
        <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: "1.1px", textTransform: "uppercase", color: ADR.label }}>{count === 1 ? "1 record" : `${count} records`}</span>
      </span>
      <button
        type="button"
        data-testid="review-saved-for-later-toggle"
        aria-expanded={open}
        onClick={onToggle}
        className="review-editable"
        style={{ flex: "none", height: 32, padding: "0 14px", borderRadius: 16, border: `1px solid ${RULE}`, background: open ? "transparent" : C.ink, color: open ? C.ink : ADR.paper, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
      >
        {open ? "Hide" : "Review now"}
      </button>
    </div>
  );
}

export function ReviewDoneCard({ title, summary, outcome, primary, secondary }: {
  title: string;
  summary: string;
  /** What the accepts have confirmed syncing to, once read. */
  outcome?: string;
  primary?: { label: string; href: string; onClick: () => void };
  secondary?: { label: string; onClick: () => void };
}) {
  // The design's finished walk ("Review"): one green bar, "Walk reviewed",
  // and Next walk → as its only strong control. The daily log is offered
  // quietly beside it, and in its place when there is no next walk.
  const pill: CSSProperties = { height: 34, padding: "0 14px", borderRadius: 17, fontSize: 13, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", fontFamily: "inherit", cursor: "pointer", textDecoration: "none" };
  return (
    <div data-testid="review-walk-clear" className="review-fade" style={{ borderRadius: 14, background: RV.greenSoft, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ flex: 1, minWidth: 0, display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 700, color: RV.greenInk }}>
          <Check size={14} color={RV.green} strokeWidth={2.8} aria-hidden style={{ flex: "none" }} />
          {title}
        </span>
        {primary && (
          <a
            data-testid="review-open-daily-report"
            href={primary.href}
            onClick={(event) => { event.preventDefault(); primary.onClick(); }}
            style={secondary
              ? { ...pill, padding: "0 6px", color: RV.greenInk, background: "transparent" }
              : { ...pill, background: C.ink, color: ADR.card }}
          >
            {primary.label}
            {!secondary && <ArrowRight size={12} color={ADR.card} aria-hidden />}
          </a>
        )}
        {secondary && (
          <button
            type="button"
            data-testid="review-next-walk"
            onClick={secondary.onClick}
            style={{ ...pill, border: 0, background: C.ink, color: ADR.card }}
          >
            {secondary.label}
            <ArrowRight size={12} color={ADR.card} aria-hidden />
          </button>
        )}
      </div>
      {summary && <div data-testid="review-done-summary" style={{ fontSize: 12.5, lineHeight: 1.45, color: RV.greenInk, opacity: 0.85, marginTop: 6, textWrap: "pretty" } as CSSProperties}>{summary}</div>}
      {outcome && <div data-testid="review-completion-outcome" role="status" style={{ fontFamily: FONT_MONO, fontSize: 9, fontWeight: 600, letterSpacing: "0.8px", textTransform: "uppercase", color: RV.greenInk, marginTop: 6 }}>{outcome}</div>}
    </div>
  );
}

/** How many of what, in words: `2 observations and 1 punch item`. */
export function countPhrase(parts: Array<[number, string, string]>): string {
  const words = parts.filter(([count]) => count > 0).map(([count, one, many]) => `${count} ${count === 1 ? one : many}`);
  if (words.length <= 1) return words[0] ?? "";
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/** The kind a decided record was, for its folded row. */
export function decidedKind(row: Pick<ReviewDecidedRowResponse, "kind" | "category" | "recordType">, autodesk: ((row: KindSource) => ChipKind) | undefined): ChipKind {
  const source = { kind: row.kind as ConsoleItemKind, category: row.category, recordType: row.recordType as ConsoleRow["recordType"] };
  return autodesk ? autodesk(source) : procoreKindOf(source);
}

// ---------------------------------------------------------------------------
// The phone's property marks, on the console
// ---------------------------------------------------------------------------

/**
 * The tokens the phone's marks colour with (`sega.css :root`, light theme),
 * set on the review card so a status ring, a priority meter, a trade or a
 * face reads exactly as it does on the phone.
 */
export const PHONE_MARK_TOKENS = {
  "--color-info": RD.info,
  "--signal-green": RD.green,
  "--signal-amber": RD.amber,
  "--destructive": RD.red,
  "--property-violet": RD.violet,
  "--property-due-soon": RD.orange,
  "--property-teal": RD.teal,
  "--muted-foreground": RD.sub,
  "--foreground": RD.ink,
  "--card": RD.card,
  "--secondary": RD.sec,
  "--avatar-tile": ADR.avatarTile,
  "--rc-meta": RD.quiet,
  "--signal-line": "rgba(20,17,12,0.10)",
} as CSSProperties;

/**
 * Which of the phone's marks a review field draws: the phone's semantic glyph
 * for a record's own properties (`recordFields` — `flow`, `bars`, `person`,
 * `pin` …) and the field key for a daily-log entry, whose glyph table keys
 * off it. The type is the phone's field type, from the daily-log definition.
 */
export function phoneMarkInputs(field: ReviewField): { glyph: string; type: RecordChipFieldType | undefined } {
  const definitionType = field.definition?.type;
  const type: RecordChipFieldType | undefined =
    field.reference === "assignee" ? "person"
    : definitionType === "number" || field.inputType === "number" ? "number"
    : definitionType === "date" || field.inputType === "date" ? "date"
    : definitionType === "time" || field.inputType === "time" ? "time"
    : definitionType === "boolean" ? "boolean"
    : definitionType === "textarea" || field.multiline ? "longText"
    : field.choices ? "choice"
    : "text";
  const glyph =
    field.reference === "location" || /^location(Text)?$/.test(field.key) ? "pin"
    : field.reference === "assignee" ? "person"
    : field.reference === "trade" ? "trade"
    : field.reference === "company" || field.key === "vendor" ? "company"
    : field.key === "dueDate" ? "calendar"
    : field.key === "status" ? "flow"
    : field.key === "priority" || field.key === "severity" ? "bars"
    : field.key === "type" ? "flag"
    : field.key === "taskCategory" ? "tag"
    : field.key;
  return { glyph, type };
}

/** The value a mark reads: a status or priority by its code in words, a
 *  switch by its stored answer, everything else by what the pill shows. */
export function phoneMarkValue(field: ReviewField, raw: string, shown: string): string {
  if (!raw) return "";
  if (field.key === "status") return raw.replace(/_/g, " ");
  if (field.key === "priority" || field.key === "severity" || field.key === "type") return raw;
  if (field.definition?.type === "boolean") return raw;
  return shown || raw;
}
